// Reading side of `dailyTodos`, the ONLY place a message sent to a human node currently lands.
//
// Messaging a human does not create a conversation and does not create a confirmable task: the
// backend's escalate_to_human writes a dailyTodos document, optionally sends an email, and parks
// that node's execution at 'waiting_for_human'. GET /pending returns [] for everybody (see
// organigram-chat-dock.service), so without this the message reaches nobody inside the app.
//
// So this is deliberately a THIN read of what exists today, not a design. The stored todo carries
// no sender, no organigram and no execution - relatedEntityId, relatedEntityType and metadata all
// arrive null - which is why nothing here tries to name who asked. Once the backend fills those in,
// this is the file that grows a sender chip; nothing else has to move.
//
// Pure on purpose (same rule as org-standard-v2.edit.ts): the spec loads it directly and must not
// pull CustomerdatabaseService in with its DI / Firebase chain.

// OFF until the Firestore rule for dailyTodos is deployed. The collection is written by the backend
// with the Admin SDK, which bypasses rules, so the client has no read grant and every load throws
// 'Missing or insufficient permissions' - once per session, in everyone's console. Nothing renders
// either way (the strip and the launcher badge both key off an empty list), so this single guard is
// what takes the feature out of a deploy.
//
// To turn it back on: flip this to true AFTER granting read on accounts/{accountId}/dailyTodos where
// resource.data.uid == request.auth.uid, and update on the `todos` field only. Everything below and
// its spec stay live in the meantime - they are pure, so they cost nothing and keep working.
export const TASKS_ENABLED = false;

// One todo, flattened out of its day document. `docId` + `index` are what a write-back needs: the
// todos live in an array field, so ticking one means rewriting the array it sits in.
export interface OrgTask {
  id: string;        // docId:index - stable for trackBy across a reload
  docId: string;
  index: number;
  text: string;
  done: boolean;
  date: string;      // YYYY-MM-DD, the day the document was raised for
  priority: number;  // 1 is highest; the backend writes 2 for everything today
}

// A day document as stored: { date, generatedAt, uid, todos: [...] }.
export interface DailyTodoDoc { id: string; data: any; }

// Documents older than this are dropped once every todo in them is done. An open todo is never
// hidden by age - an unanswered request from last week is exactly the thing worth still seeing.
export const TASK_HISTORY_DAYS = 14;

// 'YYYY-MM-DD' in LOCAL time. Not toISOString(), which is UTC and therefore names the wrong day
// either side of midnight for anyone east or west of Greenwich - the backend keys these documents
// by the user's date, so an off-by-one day would look like a missing task.
export function localDateKey(at: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

function daysBetween(a: string, b: string): number {
  const parse = (s: string) => Date.parse(`${s}T00:00:00`);
  const from = parse(a), to = parse(b);
  if (isNaN(from) || isNaN(to)) { return 0; }
  return Math.round((to - from) / 86400000);
}

// Flattens the day documents into one list: open todos first, then by day (newest first), then by
// the priority the backend assigned. Anything without text is skipped - an empty row is noise.
//
// `today` is passed in rather than read from the clock so the spec can pin the ageing rule.
export function tasksFromDocs(docs: DailyTodoDoc[], today: string): OrgTask[] {
  const out: OrgTask[] = [];
  for (const doc of docs || []) {
    const date = String(doc?.data?.date || '');
    const todos = Array.isArray(doc?.data?.todos) ? doc.data.todos : [];
    todos.forEach((todo: any, index: number) => {
      const text = String(todo?.text || '').trim();
      if (!text) { return; }
      const done = todo?.done === true;
      // Age only retires FINISHED work; an open todo stays regardless of how old it is.
      if (done && date && daysBetween(date, today) > TASK_HISTORY_DAYS) { return; }
      out.push({
        id: `${doc.id}:${index}`,
        docId: doc.id,
        index,
        text,
        done,
        date,
        priority: Number(todo?.priority) || 2
      });
    });
  }
  return out.sort((a, b) => {
    if (a.done !== b.done) { return a.done ? 1 : -1; }
    if (a.date !== b.date) { return a.date < b.date ? 1 : -1; }
    return a.priority - b.priority;
  });
}

// The date shown on a row. Relative for the two days a person thinks of by name, absolute after
// that - "5 days ago" is harder to place than a date once it is no longer recent.
export function taskDateLabel(date: string, today: string): string {
  const diff = daysBetween(date, today);
  if (!date || isNaN(Date.parse(`${date}T00:00:00`))) { return ''; }
  if (diff <= 0) { return 'Today'; }
  if (diff === 1) { return 'Yesterday'; }
  return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

// Ticking a todo rewrites the array it belongs to, because that is how it is stored - so the
// caller needs the WHOLE array back, with one entry flipped and everything else untouched.
// Returns null when the index is not there (a stale row after the document changed underneath),
// which the caller treats as "reload rather than write".
export function todosWithDone(existing: any[], index: number, done: boolean): any[] | null {
  if (!Array.isArray(existing) || !existing[index]) { return null; }
  return existing.map((todo, i) => i === index ? { ...todo, done } : todo);
}

// How many still need doing - the badge next to the section heading.
export function openTaskCount(tasks: OrgTask[]): number {
  return (tasks || []).filter(t => !t.done).length;
}
